import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Инициализация темы с синхронным применением класса
  const getInitialTheme = (): Theme => {
    // Проверяем сохраненную тему
    if (typeof window !== 'undefined') {
      const root = document.documentElement;
      const body = document.body;
      
      // Удаляем inline стиль, если он есть
      root.style.removeProperty('color-scheme');
      
      const savedTheme = localStorage.getItem('theme') as Theme | null;
      if (savedTheme === 'dark' || savedTheme === 'light') {
        // Применяем класс сразу
        if (savedTheme === 'dark') {
          root.classList.add('dark');
          body.classList.add('dark');
        } else {
          root.classList.remove('dark');
          body.classList.remove('dark');
        }
        root.setAttribute('data-theme', savedTheme);
        return savedTheme;
      }
      // Проверяем системные настройки
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        root.classList.add('dark');
        body.classList.add('dark');
        root.setAttribute('data-theme', 'dark');
        return 'dark';
      }
      
      // По умолчанию светлая тема
      root.classList.remove('dark');
      body.classList.remove('dark');
      root.setAttribute('data-theme', 'light');
    }
    return 'light';
  };

  const [theme, setThemeState] = useState<Theme>(getInitialTheme);

  useEffect(() => {
    // Применяем тему к документу
    if (typeof window !== 'undefined') {
      const root = document.documentElement;
      const body = document.body;
      
      // Проверяем текущее состояние
      const currentHasDark = root.classList.contains('dark');
      const shouldHaveDark = theme === 'dark';
      const currentDataTheme = root.getAttribute('data-theme');
      
      // Если все уже правильно, не трогаем (чтобы не конфликтовать с toggleTheme)
      if (currentHasDark === shouldHaveDark && currentDataTheme === theme) {
        console.log('Theme already correctly applied, skipping useEffect');
        return;
      }
      
      console.log('Theme applied in useEffect:', theme);
      console.log('Current state - hasDark:', currentHasDark, 'shouldHaveDark:', shouldHaveDark);
      console.log('Current data-theme:', currentDataTheme, 'should be:', theme);
      
      // Удаляем все inline стили
      root.style.removeProperty('color-scheme');
      root.style.removeProperty('colorScheme');
      
      // Удаляем все классы темы
      root.classList.remove('dark', 'light');
      body.classList.remove('dark', 'light');
      
      // Применяем класс
      if (theme === 'dark') {
        root.classList.add('dark');
        body.classList.add('dark');
      }
      // Для светлой темы просто не добавляем класс
      
      // Устанавливаем data-атрибут
      root.setAttribute('data-theme', theme);
      
      // Полностью удаляем style атрибут, если он пустой
      if (!root.style.length) {
        root.removeAttribute('style');
      }
      
      console.log('HTML classes after useEffect:', root.classList.toString());
      console.log('HTML has dark class:', root.classList.contains('dark'));
      console.log('HTML data-theme:', root.getAttribute('data-theme'));
      
      // Сохраняем в localStorage
      localStorage.setItem('theme', theme);
    }
  }, [theme]);

  // Слушаем изменения системной темы
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      // Обновляем только если тема не была явно установлена пользователем
      if (!localStorage.getItem('theme')) {
        setThemeState(e.matches ? 'dark' : 'light');
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = () => {
    console.log('=== toggleTheme FUNCTION CALLED ===');
    console.log('Current theme state:', theme);
    
    const newTheme = theme === 'light' ? 'dark' : 'light';
    console.log('New theme will be:', newTheme);
    
    // Сначала обновляем состояние, чтобы useEffect знал о новой теме
    setThemeState(newTheme);
    
    // Затем применяем класс СРАЗУ для немедленного эффекта
    if (typeof window !== 'undefined') {
      const root = document.documentElement;
      const body = document.body;
      
      console.log('HTML classes BEFORE:', root.classList.toString());
      console.log('HTML data-theme BEFORE:', root.getAttribute('data-theme'));
      
      // Удаляем все возможные inline стили
      root.style.removeProperty('color-scheme');
      root.style.removeProperty('colorScheme');
      
      // Удаляем старый класс
      root.classList.remove('dark', 'light');
      body.classList.remove('dark', 'light');
      
      // Переключаем класс
      if (newTheme === 'dark') {
        root.classList.add('dark');
        body.classList.add('dark');
        console.log('✓ Added dark class to html and body');
      } else {
        // Для светлой темы просто не добавляем класс
        console.log('✓ Removed dark class (light theme)');
      }
      
      // Устанавливаем data-атрибут СИНХРОННО
      root.setAttribute('data-theme', newTheme);
      
      // Полностью удаляем style атрибут, если он пустой
      if (!root.style.length) {
        root.removeAttribute('style');
      }
      
      console.log('HTML classes AFTER:', root.classList.toString());
      console.log('HTML has dark class:', root.classList.contains('dark'));
      console.log('HTML data-theme AFTER:', root.getAttribute('data-theme'));
      
      // Сохраняем в localStorage сразу
      localStorage.setItem('theme', newTheme);
    }
  };

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };

  const value: ThemeContextType = {
    theme,
    toggleTheme,
    setTheme,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

